import React, { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import {
    Button,
    Col,
    Glyphicon,
    Panel,
    Row,
    Section,
    Select,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { searchRadiusClick } from 'reaxl-analytics-handlers';

import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';

import useSrpNavigation from '@/hooks/useSrpNavigation';

import InventoryFiltersChipsContainer from '@/containers/srp/InventoryFiltersChipsContainer';

function AlternativeSearchActionComponent({ withCustomContent }) {

    const navigateToSrp = useSrpNavigation();
    const dispatch = useDispatch();

    const filtersValues = useSelector(srpFiltersDuck.selectors.getFiltersValues);
    const filtersOptions = useSelector(srpFiltersDuck.selectors.getFiltersOptions);
    const listingResultCount = useSelector(srpResultsDuck.selectors.getResultsCount);

    const applyOptionChange = useCallback(async (event) => {
        sendClick(searchRadiusClick, event, {
            filterName: filtersOptions?.searchRadius?.name,
        });

        // TODO we should just pass the new search radius into navigatoSrp
        // update redux state with filter change
        await dispatch(srpFiltersDuck.creators.applyFilterChange(event, filtersOptions?.searchRadius));

        navigateToSrp({ resetPagination: true, isNewSearch: false });
    }, [dispatch, navigateToSrp, filtersOptions?.searchRadius]);

    const clearAllOptions = useCallback(async () => {
        const keys = Object.keys(filtersValues);
        await dispatch(srpFiltersDuck.creators.clearFilters(keys));
        navigateToSrp({ resetPagination: true, isNewSearch: true });
    }, [dispatch, filtersValues, navigateToSrp]);

    const newSearchClasses = clsx([
        'margin-bottom-3',
        'text-right',
    ]);

    const heading = listingResultCount === 0 ? 'No results found' : 'End of results';

    return (
        <Row data-cmp="cntnr-alt-srch">
            <Col xs={12}>
                <Section
                    heading={`${heading}, but it’s not the end of the road.`}
                    uiContext="level-3"
                    headingClassName={clsx({ 'text-gray-base heading-4': withCustomContent })}
                    alignment="left"
                    className={clsx('text-normal text-blue-darker margin-bottom-4', {
                        'margin-top-3 padding-top-md-2': withCustomContent,
                    })}
                />
            </Col>
            <Col
                xs={12}
                md={6}
                lg={4}
            >
                <Panel className="padding-top-4 padding-bottom-2 padding-horizontal-3">
                    <h4
                        className="text-bold text-size-400 margin-bottom-2"
                        style={{ verticalAlign: 'middle' }}
                        id="search-distance-heading"
                    >
                        <Glyphicon
                            glyph="map-marker"
                            className="text-accent padding-right-3"
                        />
                        Expand your search distance
                    </h4>
                    <Select
                        onChange={applyOptionChange}
                        options={filtersOptions?.searchRadius?.options}
                        name={filtersOptions?.searchRadius?.name}
                        value={filtersValues?.searchRadius}
                        key="searchRadius"
                        className="margin-left-5"
                        aria-labelledby="search-distance-heading"
                    />
                </Panel>
            </Col>
            <Col
                xs={12}
                md={6}
                lg={8}
            >
                <Panel
                    className="padding-top-4 padding-bottom-2 padding-horizontal-3"
                    style={{ overflow: 'hidden' }}
                >
                    <Row>
                        <Col
                            xs={6}
                            sm={7}
                            md={6}
                            lg={7}
                        >
                            <h4
                                className="text-bold text-size-400"
                                style={{ verticalAlign: 'middle' }}
                            >
                                <Glyphicon
                                    glyph="filter"
                                    className="text-accent padding-right-3"
                                    key="filterGlyph"
                                />
                                Remove a filter
                            </h4>
                        </Col>
                        <Col
                            xs={6}
                            sm={5}
                            md={6}
                            lg={5}
                        >
                            <div
                                className={newSearchClasses}
                            >
                                <Button
                                    bsStyle="text"
                                    onClick={clearAllOptions}
                                >
                                    <Glyphicon
                                        glyph="search"
                                        className="margin-left-2 margin-right-1"
                                        key="searchGlyph"
                                    />
                                    Start a new search
                                </Button>
                            </div>
                        </Col>
                        <Col
                            xs={12}
                            sm={12}
                            md={12}
                            lg={12}
                        >
                            <div
                                className="padding-2 margin-left-4"
                            >
                                <InventoryFiltersChipsContainer
                                    key="filterChips"
                                />
                            </div>
                        </Col>
                    </Row>
                </Panel>
            </Col>
        </Row>
    );
}

AlternativeSearchActionComponent.propTypes = {
    withCustomContent: PropTypes.bool,
};

export const AlternativeSearchAction = React.memo(AlternativeSearchActionComponent);

export default AlternativeSearchAction;
